import { useId, useState } from "react";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { asTeam, type TeamMember } from "../lib/section-data";
import { plainText } from "../lib/richtext";
import { ResponsiveImage } from "../components/ResponsiveImage";
import { RichText } from "../components/RichText";
import { RevealItem, RevealList } from "../components/motion";
import { SectionHeading, SectionShell, type SectionProps } from "./SectionRenderer";

/**
 * Roughly the length at which a bio outgrows three lines on a card.
 *
 * Below it there is nothing behind the clamp, so the card offers no tap
 * affordance and does not respond to one — a "Read full bio" that reveals
 * nothing is worse than no control at all.
 */
const CLAMP_THRESHOLD = 160;

const COLUMNS: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

/**
 * At or below this many people, the portrait grid is the wrong shape.
 *
 * Four columns holding two members leaves half the row empty, and the fix is
 * not simply fewer columns: two portrait cards stretched across a wide
 * container become billboards, and their bios stay clamped to three lines with
 * the rest hidden behind a hover the visitor has no reason to try. A small team
 * gets the profile row instead — portrait, identity and bio side by side, using
 * the full width to show the whole bio rather than to enlarge a photograph.
 */
const FEATURE_MAX_MEMBERS = 2;

export default function TeamBlock({
  content,
  settings,
  data,
}: SectionProps<BlockContentMap["team"]>) {
  const all = asTeam(data) ?? [];

  // An explicit selection wins and keeps the editor's order; empty means
  // "everyone published", so a new joiner appears without anyone editing this.
  const members =
    content.teamMemberIds.length > 0
      ? content.teamMemberIds
          .map((id) => all.find((member) => member.id === id))
          .filter((member): member is (typeof all)[number] => Boolean(member))
      : all;

  if (members.length === 0) return null;

  // The editor's column count is a maximum, not a promise. Three people in a
  // four-column grid leave one cell empty and the row visibly off-centre, so
  // the grid narrows to what there is to put in it.
  const gridColumns = Math.min(content.columns, members.length);

  return (
    <SectionShell settings={settings}>
      <SectionHeading
        eyebrow={content.eyebrow}
        heading={content.heading}
        intro={content.intro}
        settings={settings}
      />

      {members.length <= FEATURE_MAX_MEMBERS ? (
        <RevealList className="grid gap-8">
          {members.map((member) => (
            <RevealItem key={member.id}>
              <TeamProfileRow
                member={member}
                showBio={content.showBio}
                showCredentials={content.showCredentials}
              />
            </RevealItem>
          ))}
        </RevealList>
      ) : (
        <RevealList className={cn("grid gap-8", COLUMNS[gridColumns] ?? COLUMNS[3])}>
          {members.map((member) => (
            <RevealItem key={member.id}>
              <TeamCard
                member={member}
                showBio={content.showBio}
                showCredentials={content.showCredentials}
              />
            </RevealItem>
          ))}
        </RevealList>
      )}
    </SectionShell>
  );
}

/**
 * One person, given a full row.
 *
 * Three columns on a wide screen — portrait, identity, bio — so the width the
 * grid would have left empty is spent on the bio instead. Nothing is clamped
 * here and nothing hides behind a hover: with two people on the page there is
 * room to simply print what they have done, which is the point of the section.
 *
 * That also drops the tap-to-expand button the card variant needs, because
 * there is no longer a hidden state for a touch visitor to be locked out of.
 */
function TeamProfileRow({
  member,
  showBio,
  showCredentials,
}: {
  member: TeamMember;
  showBio: boolean;
  showCredentials: boolean;
}) {
  const credentials = showCredentials ? member.credentials : [];
  const hasBio = Boolean(showBio && member.bio);

  return (
    <article
      className={cn(
        "grid gap-6 rounded-lg bg-white p-6 text-left shadow-card",
        // The lift the portrait cards use is wrong at this size — a slab this
        // wide sliding upward reads as the page shifting, not a card
        // responding. The shadow alone says the same thing quietly.
        "transition-shadow duration-300 hover:shadow-lg motion-reduce:transition-none",
        // Two columns from `md`: portrait beside a stack of identity and bio,
        // the portrait spanning both rows. Three from `lg`, where there is
        // width for the bio to sit alongside rather than beneath.
        "md:grid-cols-[13rem_minmax(0,1fr)] md:items-start md:gap-8 md:p-8",
        // Without a bio there is no third column to fill, and asking for one
        // would open a gap wider than the two columns that hold anything.
        hasBio ? "lg:grid-cols-[15rem_17rem_minmax(0,1fr)] lg:gap-10" : "lg:grid-cols-[15rem_1fr]",
      )}
    >
      <div className="md:row-span-2 lg:row-span-1">
        {member.photoUrl ? (
          <ResponsiveImage
            image={{ url: member.photoUrl, alt: `${member.name}, ${member.role}` }}
            sizes="(min-width: 1024px) 15rem, (min-width: 768px) 13rem, 13rem"
            className="aspect-[3/4] w-full max-w-[13rem] rounded-md object-cover md:max-w-none"
          />
        ) : (
          <div
            aria-hidden
            className="flex aspect-[3/4] w-full max-w-[13rem] items-center justify-center rounded-md bg-navy-900 md:max-w-none"
          >
            <span className="font-display text-5xl text-gold-300">{initials(member.name)}</span>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-2xl text-navy-900 lg:text-3xl">{member.name}</h3>
        <p className="mt-1 text-sm text-gold-700">{member.role}</p>

        {/*
          One credential per line rather than the card's `·`-joined run. These
          two carry five qualifications each; strung together they read as a
          single long string of abbreviations, and the column exists precisely
          so they can be scanned as the list they are.
        */}
        {credentials.length > 0 && (
          <ul className="mt-5 space-y-2 border-t border-surface-200 pt-5 text-xs uppercase tracking-wider text-ink-500">
            {credentials.map((credential) => (
              <li key={credential} className="flex gap-2.5">
                <span aria-hidden className="mt-1.5 size-1 shrink-0 rounded-full bg-gold-500" />
                {credential}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showBio && member.bio && (
        <div className="max-w-prose lg:border-l lg:border-surface-200 lg:pl-10">
          <RichText doc={member.bio} />
        </div>
      )}
    </article>
  );
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");
}

/**
 * One person.
 *
 * The bio is clamped to three lines so a row of cards is one height, and the
 * rest arrives on hover — except that a touch screen has no hover, which left
 * every phone visitor with a bio permanently cut off at three lines. So the
 * card is also a tap target below `md`: touch it and the bio opens in place.
 *
 * Desktop is untouched. Everything the tap state changes is either scoped to
 * below `md` or a no-op above it, and the tap target itself is `display: none`
 * from `md` up, so a pointer user sees exactly the hover overlay as before.
 */
function TeamCard({
  member,
  showBio,
  showCredentials,
}: {
  member: TeamMember;
  showBio: boolean;
  showCredentials: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const bioId = useId();

  const hasBio = Boolean(showBio && member.bio);
  // Only a bio with something behind the clamp is worth opening.
  const expandable = hasBio && plainText(member.bio).length > CLAMP_THRESHOLD;

  return (
    /*
      The whole card lifts — portrait, name, role and all — rather than the
      photograph alone, so the person reads as one object rising off the page
      instead of an image that moves independently of its caption.

      `motion-reduce` removes the travel itself, not merely its transition. A
      reduced-motion setting is a request not to be moved; an instant jump to a
      translated position honours the letter of it and none of the intent.
    */
    <article
      className={cn(
        // The portrait and caption share one card surface, distinct from the
        // section background like every other card.
        "group relative rounded-lg bg-white shadow-card transition-[transform,box-shadow] duration-300 ease-out",
        "hover:-translate-y-1.5 hover:shadow-xl",
        "focus-within:-translate-y-1.5 focus-within:shadow-xl",
        "motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        "motion-reduce:focus-within:translate-y-0",
      )}
    >
      {/*
        The tap target is the card, because on a phone a three-line paragraph is
        a poor one — but it is a real button covering the card rather than a
        click handler bolted to the <article>, so it is reachable by keyboard,
        announces its state, and says whose bio it opens. It does not exist from
        `md` up, where hover already does this job.
      */}
      {expandable && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bioId}
          onClick={() => setExpanded((open) => !open)}
          className="absolute inset-0 z-20 rounded-lg md:hidden"
        >
          <span className="sr-only">
            {expanded ? `Show less about ${member.name}` : `Read the full bio for ${member.name}`}
          </span>
        </button>
      )}

      {member.photoUrl ? (
        <ResponsiveImage
          image={{ url: member.photoUrl, alt: `${member.name}, ${member.role}` }}
          sizes="(min-width: 1024px) 25vw, 50vw"
          className="aspect-[3/4] w-full rounded-t-lg object-cover"
        />
      ) : (
        <div
          aria-hidden
          className="flex aspect-[3/4] w-full items-center justify-center rounded-t-lg bg-navy-900"
        >
          <span className="font-display text-4xl text-gold-300">{initials(member.name)}</span>
        </div>
      )}

      <div className="p-5">
        <h3 className="font-display text-xl text-navy-900">{member.name}</h3>
        <p className="text-sm text-gold-700">{member.role}</p>

        {showCredentials && member.credentials.length > 0 && (
          <p className="mt-1 text-xs uppercase tracking-wider text-ink-500">
            {member.credentials.join(" · ")}
          </p>
        )}

        {hasBio && (
          <div className="relative mt-3">
            {/*
              The readable copy, and the only one assistive technology sees.
              `line-clamp` is overflow, not concealment — the full text is in
              the accessible tree whatever the clamp shows — so the hover
              overlay below is the duplicate, and it is the one hidden from
              screen readers rather than this.
            */}
            <div
              id={bioId}
              className={cn(
                "text-sm [&_p]:text-sm",
                // Open on mobile means unclamped on mobile. From `md` the clamp
                // is unconditional: that is the desktop card, hover and all.
                expanded ? "md:line-clamp-3" : "line-clamp-3",
              )}
            >
              <RichText doc={member.bio} />
            </div>

            {/*
              The cue, not the control — the button covering the card is what
              anyone actually presses, and it carries the label screen readers
              hear. This is here so a sighted visitor can see there is more to
              read, and is hidden from assistive technology to avoid announcing
              the same affordance twice.
            */}
            {expandable && (
              <span
                aria-hidden
                className="mt-2 block text-xs font-semibold uppercase tracking-wider text-gold-700 md:hidden"
              >
                {expanded ? "Show less" : "Read full bio"}
              </span>
            )}

            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute -inset-x-4 -top-4 z-10 rounded-lg p-4",
                "bg-surface-0 shadow-lg ring-1 ring-ink-200",
                "text-sm opacity-0 [&_p]:text-sm",
                // Never on mobile: there the bio opens in place, and an overlay
                // left in play would be triggered by the sticky :hover some
                // touch browsers apply on tap — two reveals fighting over one
                // card.
                "hidden md:block",
                // Revealed by focus as well as hover: the card is not a
                // control, but a keyboard user tabbing through a link inside it
                // should not be the only person who cannot read the bio.
                "transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100",
                "motion-reduce:transition-none",
              )}
            >
              <RichText doc={member.bio} />
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
